/**
 * Distribution parameter editor.
 *
 * Every default the UI applies is shown and editable rather than baked in
 * (PRD §4.1): "±1% means uniform" is a modelling assumption, not a fact, and an
 * engineer must be able to see it and change it. The tolerance helper writes
 * concrete parameters into the model instead of storing a shorthand, so what is
 * analysed is always what is displayed.
 */

import type { Distribution } from '../../engine/distributions.ts';
import { mean, stdDev, quantile, nominal } from '../../engine/distributions.ts';
import { MeasuredDataImport } from './MeasuredDataImport.tsx';
import { eng, sig } from '../format.ts';
import { Info } from '../Info.tsx';

interface Props {
  value: Distribution;
  unit?: string;
  onChange: (d: Distribution) => void;
}

const KINDS: { value: Distribution['kind']; label: string }[] = [
  { value: 'uniform', label: 'Uniform' },
  { value: 'normal', label: 'Normal' },
  { value: 'truncnormal', label: 'Truncated normal' },
  { value: 'triangular', label: 'Triangular' },
  { value: 'lognormal', label: 'Lognormal' },
  { value: 'discrete', label: 'Discrete set' },
  { value: 'fixed', label: 'Fixed' },
];

// 'empirical' is absent by design: measured data arrives from a file, and
// offering it in a dropdown would invite fabricating measurements that were
// never taken. An existing empirical distribution still renders and can be
// converted away from.

/** Convert between kinds while preserving centre and spread where meaningful. */
function convert(d: Distribution, kind: Distribution['kind']): Distribution {
  const m = mean(d);
  const s = stdDev(d) || Math.abs(m) * 0.01 || 0.01;

  switch (kind) {
    case 'fixed':
      return { kind: 'fixed', value: m };
    case 'uniform':
      return { kind: 'uniform', min: m - s * Math.sqrt(3), max: m + s * Math.sqrt(3) };
    case 'normal':
      return { kind: 'normal', mu: m, sigma: s };
    case 'truncnormal':
      return { kind: 'truncnormal', mu: m, sigma: s, min: m - 3 * s, max: m + 3 * s };
    case 'triangular':
      return { kind: 'triangular', min: m - s * Math.sqrt(6), mode: m, max: m + s * Math.sqrt(6) };
    case 'lognormal': {
      const positive = m > 0 ? m : 1;
      const cv = s / positive;
      return {
        kind: 'lognormal',
        muLog: Math.log(positive / Math.sqrt(1 + cv * cv)),
        sigmaLog: Math.sqrt(Math.log(1 + cv * cv)),
      };
    }
    case 'discrete':
      return { kind: 'discrete', values: [m - s, m, m + s], weights: [1, 2, 1] };
    case 'empirical': {
      // Not offered in the picker — empirical distributions arrive from
      // measured data. Reached only if a project already holds one, so the
      // source is discretised at evenly spaced quantiles rather than
      // fabricating measurements that were never taken.
      const n = 201;
      const samples = Array.from({ length: n }, (_, i) => quantile(d, (i + 0.5) / n));
      return { kind: 'empirical', samples };
    }
  }
}

function Num({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : ''}
        step={step ?? 'any'}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) onChange(v);
        }}
      />
    </label>
  );
}

export function DistributionEditor({ value, unit, onChange }: Props) {
  const d = value;

  return (
    <div className="dist-editor">
      <label className="field">
        <span>
          Distribution <Info topic="distribution" />
        </span>
        <select
          value={d.kind}
          onChange={(e) => onChange(convert(d, e.target.value as Distribution['kind']))}
        >
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </label>

      {d.kind === 'fixed' && (
        <Num label="Value" value={d.value} onChange={(v) => onChange({ ...d, value: v })} />
      )}

      {d.kind === 'uniform' && (
        <>
          <Num label="Min" value={d.min} onChange={(v) => onChange({ ...d, min: v })} />
          <Num label="Max" value={d.max} onChange={(v) => onChange({ ...d, max: v })} />
        </>
      )}

      {d.kind === 'normal' && (
        <>
          <Num label="Mean μ" value={d.mu} onChange={(v) => onChange({ ...d, mu: v })} />
          <Num label="Std dev σ" value={d.sigma} onChange={(v) => onChange({ ...d, sigma: v })} />
        </>
      )}

      {d.kind === 'truncnormal' && (
        <>
          <Num label="Mean μ" value={d.mu} onChange={(v) => onChange({ ...d, mu: v })} />
          <Num label="Std dev σ" value={d.sigma} onChange={(v) => onChange({ ...d, sigma: v })} />
          <Num label="Hard min" value={d.min} onChange={(v) => onChange({ ...d, min: v })} />
          <Num label="Hard max" value={d.max} onChange={(v) => onChange({ ...d, max: v })} />
        </>
      )}

      {d.kind === 'triangular' && (
        <>
          <Num label="Min" value={d.min} onChange={(v) => onChange({ ...d, min: v })} />
          <Num label="Mode" value={d.mode} onChange={(v) => onChange({ ...d, mode: v })} />
          <Num label="Max" value={d.max} onChange={(v) => onChange({ ...d, max: v })} />
        </>
      )}

      {d.kind === 'lognormal' && (
        <>
          <Num label="μ (log)" value={d.muLog} onChange={(v) => onChange({ ...d, muLog: v })} />
          <Num
            label="σ (log)"
            value={d.sigmaLog}
            onChange={(v) => onChange({ ...d, sigmaLog: v })}
          />
        </>
      )}

      {d.kind === 'empirical' && (
        <div className="field wide">
          <span>Measured data</span>
          <p className="dist-summary">
            {d.samples.length.toLocaleString('en-US')} samples, resampled by interpolating
            their empirical distribution. Re-import to replace them.
          </p>
        </div>
      )}

      {d.kind === 'discrete' && (
        <label className="field wide">
          <span>Values and weights</span>
          <input
            type="text"
            value={d.values.map((v, i) => `${v}:${d.weights[i]}`).join(', ')}
            onChange={(e) => {
              const pairs = e.target.value.split(',').map((p) => p.trim()).filter(Boolean);
              const values: number[] = [];
              const weights: number[] = [];
              for (const p of pairs) {
                const [v, w] = p.split(':');
                const vn = Number(v);
                const wn = w === undefined ? 1 : Number(w);
                if (Number.isFinite(vn) && Number.isFinite(wn) && wn >= 0) {
                  values.push(vn);
                  weights.push(wn);
                }
              }
              if (values.length > 0) onChange({ kind: 'discrete', values, weights });
            }}
            placeholder="5.9:1, 6.0:3, 6.1:1"
          />
        </label>
      )}

      {/* Tolerance shorthands write concrete parameters into the model rather
          than storing a shorthand, so what is analysed is always what is shown.
          Which distribution a "±1%" part really has is a modelling assumption
          (PRD §4.1) — the buttons apply one and leave it visible and editable. */}
      {d.kind !== 'fixed' && d.kind !== 'empirical' && d.kind !== 'discrete' && (
        <div className="preset-row">
          <span className="preset-label">±</span>
          {[0.001, 0.005, 0.01, 0.05].map((tol) => (
            <button
              key={tol}
              type="button"
              className="preset-btn"
              onClick={() => {
                const c = nominal(d);
                const half = Math.abs(c * tol) || tol;
                onChange(
                  d.kind === 'normal'
                    ? { kind: 'normal', mu: c, sigma: half / 3 }
                    : d.kind === 'truncnormal'
                      ? { kind: 'truncnormal', mu: c, sigma: half / 3, min: c - half, max: c + half }
                      : d.kind === 'triangular'
                        ? { kind: 'triangular', min: c - half, mode: c, max: c + half }
                        : { kind: 'uniform', min: c - half, max: c + half },
                );
              }}
              title={`Set the band to ±${tol * 100}% of ${sig(nominal(d), 4)}`}
            >
              {tol * 100}%
            </button>
          ))}
          <span className="preset-note">
            {d.kind === 'normal' || d.kind === 'truncnormal' ? 'band = 3σ' : 'full band'}
          </span>
        </div>
      )}

      <MeasuredDataImport unit={unit} onImport={onChange} />

      {/* Whatever the parameterisation, the resulting moments are shown, so a
          user can sanity-check the model against the number they had in mind. */}
      <p className="dist-summary">
        mean {eng(mean(d), 4, unit)} · σ {eng(stdDev(d), 3, unit)}
        {stdDev(d) > 0 && Math.abs(mean(d)) > 0 && (
          <> · {sig((stdDev(d) / Math.abs(mean(d))) * 100, 3)}% CV</>
        )}
      </p>
    </div>
  );
}
